import { useEffect, useRef, useState } from 'react';
import { Upload, FolderOpen, FileJson, Loader2, ShieldCheck, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { catalogApi, type BulkAllowlistFile, type BulkAllowlistResult } from '@/api/endpoints';
import { toast } from '@/store/toast';
import { useAuthStore } from '@/store/auth';

type Stage = 'idle' | 'hashing' | 'previewing' | 'importing' | 'done';

export function BulkImportDialog({ onImported }: { onImported: () => void }) {
  const { isAdmin } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'folder' | 'json'>('folder');
  const [stage, setStage] = useState<Stage>('idle');
  const [localDirLabel, setLocalDirLabel] = useState('');
  const [hashedFiles, setHashedFiles] = useState<BulkAllowlistFile[]>([]);
  const [hashProgress, setHashProgress] = useState({ done: 0, total: 0, current: '' });
  const [preview, setPreview] = useState<BulkAllowlistResult | null>(null);
  const workerRef = useRef<Worker | null>(null);
  const startedAtRef = useRef<number>(0);
  const [elapsed, setElapsed] = useState(0);

  // Cleanup ao fechar
  useEffect(() => {
    if (!open) {
      workerRef.current?.terminate();
      workerRef.current = null;
      setStage('idle');
      setHashedFiles([]);
      setHashProgress({ done: 0, total: 0, current: '' });
      setPreview(null);
      setLocalDirLabel('');
    }
  }, [open]);

  // Timer pra mostrar tempo decorrido durante o hashing
  useEffect(() => {
    if (stage !== 'hashing') return;
    startedAtRef.current = Date.now();
    const t = window.setInterval(() => setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000)), 500);
    return () => clearInterval(t);
  }, [stage]);

  if (!isAdmin()) return null;

  // ============ Hashing de pasta via Web Worker ============
  function onFolderSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const fl = e.target.files;
    if (!fl || fl.length === 0) return;
    const files: { file: File; relPath: string }[] = [];
    let detectedRoot: string | null = null;
    for (let i = 0; i < fl.length; i++) {
      const f = fl[i];
      // webkitRelativePath = "C:\source\Game\file.rez" relativo a pasta selecionada
      const rel = (f as any).webkitRelativePath || f.name;
      const slash = rel.indexOf('/');
      if (slash > -1 && detectedRoot === null) detectedRoot = rel.substring(0, slash);
      files.push({ file: f, relPath: rel });
    }
    if (detectedRoot && !localDirLabel) setLocalDirLabel(detectedRoot);

    if (files.length > 50_000) {
      toast.error('Muitos arquivos', `Esta pasta tem ${files.length.toLocaleString('pt-BR')} arquivos. Limite recomendado: 50.000.`);
      return;
    }

    setStage('hashing');
    setHashProgress({ done: 0, total: files.length, current: 'iniciando…' });
    setElapsed(0);

    // Cria worker (Vite suporta new URL('./...worker.ts', import.meta.url))
    const worker = new Worker(new URL('../../workers/hashing.worker.ts', import.meta.url), { type: 'module' });
    workerRef.current = worker;
    worker.onmessage = (ev) => {
      const msg = ev.data;
      if (msg.type === 'progress') {
        setHashProgress({ done: msg.done, total: msg.total, current: msg.current });
      } else if (msg.type === 'done') {
        setHashedFiles(msg.results);
        setStage('previewing');
        runDryRun(msg.results);
        worker.terminate();
        workerRef.current = null;
      } else if (msg.type === 'error') {
        toast.error('Erro ao processar hashes', msg.message);
        setStage('idle');
      }
    };
    worker.postMessage({ type: 'start', files });
  }

  // ============ Upload de JSON ============
  async function onJsonSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const arr: any[] = Array.isArray(parsed) ? parsed : (Array.isArray(parsed?.files) ? parsed.files : []);
      if (arr.length === 0) {
        toast.error('JSON inválido', 'Esperado: array de objetos com {md5, sha256, name, ext, relPath}.');
        return;
      }
      const normalized: BulkAllowlistFile[] = arr.map(it => ({
        name: it.name ?? it.fileName ?? '',
        relPath: it.relPath ?? it.path ?? it.fullName ?? '',
        ext: (it.ext ?? '').toLowerCase().replace(/^\./, ''),
        size: Number(it.size ?? 0),
        md5: (it.md5 ?? '').toLowerCase(),
        sha256: (it.sha256 ?? it.sha ?? '').toLowerCase()
      })).filter(it => it.md5 || it.sha256);
      if (normalized.length === 0) {
        toast.error('JSON vazio', 'Nenhuma entrada com md5 ou sha256 válido.');
        return;
      }
      setHashedFiles(normalized);
      setStage('previewing');
      runDryRun(normalized);
    } catch (err: any) {
      toast.error('Falha ao ler JSON', err.message);
    }
  }

  // ============ Dry-run e confirmacao ============
  async function runDryRun(files: BulkAllowlistFile[]) {
    try {
      const r = await catalogApi.bulkAllowlist({
        files,
        localDirLabel: localDirLabel || undefined,
        dryRun: true
      });
      setPreview(r);
    } catch (e: any) {
      toast.error('Falha ao analisar', e?.response?.data?.reason ?? e.message);
      setStage('idle');
    }
  }

  async function confirmImport() {
    if (!preview) return;
    setStage('importing');
    try {
      const r = await catalogApi.bulkAllowlist({
        files: hashedFiles,
        localDirLabel: localDirLabel || undefined,
        dryRun: false
      });
      setPreview(r);
      setStage('done');
      toast.success(
        'Importação concluída',
        `${r.marks.totalAffected} marca(s) ALLOWLIST aplicadas (${r.marks.created} criadas, ${r.marks.updated} atualizadas).`
      );
      onImported();
    } catch (e: any) {
      toast.error('Falha ao importar', e?.response?.data?.reason ?? e.message);
      setStage('previewing');
    }
  }

  const pct = hashProgress.total > 0 ? Math.round((hashProgress.done / hashProgress.total) * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Upload className="h-3 w-3 mr-1" /> Importar verificados
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Importar arquivos verificados em massa</DialogTitle>
        </DialogHeader>

        {stage === 'idle' && (
          <Tabs value={tab} onValueChange={v => setTab(v as any)}>
            <TabsList className="w-full">
              <TabsTrigger value="folder" className="flex-1"><FolderOpen className="h-3 w-3 mr-1" /> Pasta local</TabsTrigger>
              <TabsTrigger value="json" className="flex-1"><FileJson className="h-3 w-3 mr-1" /> Arquivo JSON</TabsTrigger>
            </TabsList>

            <TabsContent value="folder">
              <div className="space-y-3 py-2">
                <div className="text-sm text-muted-foreground bg-muted/40 border-l-2 border-info p-2 rounded">
                  Selecione a pasta que contém os arquivos legítimos do jogo. Os hashes (MD5 + SHA-256) são calculados no seu navegador — os arquivos não são enviados ao servidor, apenas os hashes.
                </div>
                <div>
                  <Label>Rótulo (aparece nas notas das marcas)</Label>
                  <Input
                    value={localDirLabel}
                    onChange={e => setLocalDirLabel(e.target.value)}
                    placeholder={'Ex.: C:\\source\\ — Combat Arms Brasil Server'}
                  />
                </div>
                <div>
                  <Label>Pasta com os binários verificados</Label>
                  <input
                    type="file"
                    /* @ts-expect-error webkitdirectory existe mas nao no JSX padrao */
                    webkitdirectory=""
                    directory=""
                    multiple
                    onChange={onFolderSelected}
                    className="block w-full text-sm file:mr-2 file:py-1.5 file:px-3 file:border file:border-input file:rounded-md file:text-xs file:bg-background file:text-foreground hover:file:bg-accent"
                  />
                </div>
                <div className="text-xs text-muted-foreground">
                  Limite: 50.000 arquivos por importação. Arquivos &gt; 200 MB recebem só MD5 (SHA-256 é pulado por custo de memória).
                </div>
              </div>
            </TabsContent>

            <TabsContent value="json">
              <div className="space-y-3 py-2">
                <div className="text-sm text-muted-foreground bg-muted/40 border-l-2 border-info p-2 rounded">
                  Envie um JSON com array de objetos no formato {'{ name, ext, md5, sha256, size, relPath }'}. Útil quando os hashes já foram calculados em outro ambiente (PowerShell, script de CI etc.).
                </div>
                <div>
                  <Label>Rótulo (aparece nas notas das marcas)</Label>
                  <Input
                    value={localDirLabel}
                    onChange={e => setLocalDirLabel(e.target.value)}
                    placeholder={'Ex.: build CI #1234 — Combat Arms'}
                  />
                </div>
                <div>
                  <Label>Arquivo JSON</Label>
                  <input
                    type="file"
                    accept=".json,application/json"
                    onChange={onJsonSelected}
                    className="block w-full text-sm file:mr-2 file:py-1.5 file:px-3 file:border file:border-input file:rounded-md file:text-xs file:bg-background file:text-foreground hover:file:bg-accent"
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>
        )}

        {stage === 'hashing' && (
          <div className="space-y-3 py-4">
            <div className="flex items-center gap-2 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              Calculando hashes localmente — <strong>{hashProgress.done}</strong> de <strong>{hashProgress.total}</strong> ({pct}%) — {elapsed}s
            </div>
            <div className="h-2 bg-muted rounded overflow-hidden">
              <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
            </div>
            <div className="text-xs text-muted-foreground truncate">Atual: {hashProgress.current}</div>
          </div>
        )}

        {(stage === 'previewing' || stage === 'importing' || stage === 'done') && preview && (
          <div className="space-y-3 py-2">
            {stage === 'done' ? (
              <div className="flex items-center gap-2 text-success">
                <ShieldCheck className="h-5 w-5" />
                <span className="font-medium">Importação concluída com sucesso</span>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground bg-muted/40 border-l-2 border-info p-2 rounded">
                Pré-visualização da importação. Confira os números abaixo antes de confirmar.
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 text-sm">
              <Stat label="Arquivos analisados" value={preview.totalFiles.toLocaleString('pt-BR')} />
              <Stat label="Hashes fornecidos" value={`${preview.totalHashesProvided.md5} MD5 + ${preview.totalHashesProvided.sha256} SHA`} />
              <Stat label="Match em detecções" value={`${preview.matchedInDetections.md5} MD5 + ${preview.matchedInDetections.sha256} SHA`} tone="success" />
              <Stat
                label={stage === 'done' ? 'Marcas aplicadas' : 'Marcas que serão criadas/atualizadas'}
                value={stage === 'done'
                  ? `${preview.marks.totalAffected} (${preview.marks.created} novas, ${preview.marks.updated} atualizadas)`
                  : preview.marks.wouldCreate.toLocaleString('pt-BR')
                }
                tone="success"
              />
            </div>

            {preview.sampleMatches.length > 0 && (
              <div className="bg-muted/40 rounded p-2 text-xs space-y-1 max-h-40 overflow-auto">
                <div className="text-muted-foreground mb-1">Primeiros matches:</div>
                {preview.sampleMatches.map((s, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px]">{s.kind}</Badge>
                    <span className="font-mono text-[10px] truncate">{s.value.substring(0, 16)}…</span>
                    <span className="text-muted-foreground truncate">{s.relPath || s.name}</span>
                  </div>
                ))}
              </div>
            )}

            {stage === 'previewing' && preview.marks.wouldCreate === 0 && (
              <div className="flex items-center gap-2 text-warning text-sm">
                <AlertCircle className="h-4 w-4" />
                Nenhum hash bateu com detecções existentes. Não há marcas para criar.
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {stage === 'idle' && <Button variant="ghost" onClick={() => setOpen(false)}>Fechar</Button>}
          {stage === 'hashing' && (
            <Button variant="ghost" onClick={() => { workerRef.current?.terminate(); setStage('idle'); }}>Cancelar</Button>
          )}
          {stage === 'previewing' && (
            <>
              <Button variant="ghost" onClick={() => setStage('idle')}>Voltar</Button>
              <Button onClick={confirmImport} disabled={!preview || preview.marks.wouldCreate === 0}>
                <ShieldCheck className="h-3 w-3 mr-1" /> Confirmar importação
              </Button>
            </>
          )}
          {stage === 'importing' && <Button disabled><Loader2 className="h-3 w-3 animate-spin mr-1" /> Importando…</Button>}
          {stage === 'done' && <Button onClick={() => setOpen(false)}>Fechar</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'default' | 'success' | 'danger' }) {
  const cls = tone === 'success' ? 'text-success' : tone === 'danger' ? 'text-destructive' : '';
  return (
    <div className="border rounded p-2">
      <div className="text-[10px] uppercase text-muted-foreground">{label}</div>
      <div className={`text-lg font-bold tabular-nums ${cls}`}>{value}</div>
    </div>
  );
}
